import React, { useEffect, useState, useRef, Fragment } from 'react';
import { SearchIcon, ChevronDownIcon, ChevronUpIcon, TrendingDownIcon, DollarSignIcon, HomeIcon, CheckIcon, ArrowUpDownIcon } from 'lucide-react';
import { Retention } from '../types';
interface FundedBorrowersProps {
  data: Retention[];
}
const TYPE_OPTIONS = ['Conventional', 'FHA', 'VA', 'Jumbo'];
const FUNDED_OPTIONS = ['Last 3 Months', '3-6 Months Ago', '6+ Months Ago'];
const AMOUNT_OPTIONS = ['Under $300k', '$300k – $500k', 'Over $500k'];
const RATE_OPTIONS = ['Above 7%', '6.5%-7%', 'Below 6.5%'];
function monthsSince(dateStr: string): number {
  const then = new Date(dateStr).getTime();
  const now = Date.now();
  return (now - then) / (1000 * 60 * 60 * 24 * 30.44);
}
export function FundedBorrowers({
  data
}: FundedBorrowersProps) {
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [rateFilter, setRateFilter] = useState<string | null>(null);
  const [amountFilter, setAmountFilter] = useState<string | null>(null);
  const [fundedFilter, setFundedFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  // Ensure data has the required fields for display
  const displayData = data.map(d => ({
    ...d,
    rate: d.rate || 6.875,
    loanType: d.loanType || 'Conventional',
    lastContact: d.lastContact || '3/15/2025 02:30 PM'
  }));
  const filteredData = displayData.filter(d => {
    if (!d.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (typeFilter && d.loanType !== typeFilter) return false;
    if (rateFilter) {
      if (rateFilter === 'Above 7%' && !(d.rate > 7)) return false;
      if (rateFilter === '6.5%-7%' && !(d.rate >= 6.5 && d.rate <= 7)) return false;
      if (rateFilter === 'Below 6.5%' && !(d.rate < 6.5)) return false;
    }
    if (amountFilter) {
      if (amountFilter === 'Under $300k' && !(d.amount < 300000)) return false;
      if (amountFilter === '$300k – $500k' && !(d.amount >= 300000 && d.amount <= 500000)) return false;
      if (amountFilter === 'Over $500k' && !(d.amount > 500000)) return false;
    }
    if (fundedFilter) {
      const m = monthsSince(d.originalLoanDate);
      if (fundedFilter === 'Last 3 Months' && !(m <= 3)) return false;
      if (fundedFilter === '3-6 Months Ago' && !(m > 3 && m <= 6)) return false;
      if (fundedFilter === '6+ Months Ago' && !(m > 6)) return false;
    }
    return true;
  });
  return <div className="flex flex-col h-full bg-theme-app" data-id="element-96">
      {/* Tabs */}
      <div className="flex items-center gap-6 px-6 pt-4 border-b border-olympian-border shrink-0 bg-olympian-surface/50" data-id="element-97">
        <button className="pb-3 text-sm font-semibold flex items-center gap-2 border-b-2 border-olympian-gold text-olympian-gold" data-id="element-98">
          My Funded Borrowers
          <span className="text-xs px-2 py-0.5 rounded-full bg-olympian-gold/20 text-olympian-gold" data-id="element-99">
            {data.length}
          </span>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 custom-scrollbar" data-id="element-100">
        <div className="bg-olympian-surface border border-olympian-border rounded-xl overflow-hidden flex flex-col shadow-sm" data-id="element-101">
          {/* Header */}
          <div className="p-6 border-b border-olympian-border bg-olympian-surface" data-id="element-102">
            <h2 className="text-lg font-bold text-theme mb-1" data-id="element-103">
              Funded Borrowers
            </h2>
            <p className="text-sm text-olympian-textMuted" data-id="element-104">
              {filteredData.length} borrowers eligible for follow-up · Click a
              row for talking points
            </p>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-olympian-border flex items-center gap-3 bg-olympian-navy/30" data-id="element-105">
            <div className="relative flex-1 max-w-xs" data-id="element-106">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-olympian-textMuted" data-id="element-107" />
              <input type="text" placeholder="Search by name..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-olympian-navy border border-olympian-border rounded-lg text-sm text-theme focus:outline-none focus:border-olympian-gold transition-colors" data-id="element-108" />
            </div>
            <FilterDropdown label="Rate" options={RATE_OPTIONS} value={rateFilter} onChange={setRateFilter} data-id="element-109" />
            <FilterDropdown label="Amount" options={AMOUNT_OPTIONS} value={amountFilter} onChange={setAmountFilter} data-id="element-110" />
            <FilterDropdown label="Funded" options={FUNDED_OPTIONS} value={fundedFilter} onChange={setFundedFilter} data-id="element-111" />
            <FilterDropdown label="Type" options={TYPE_OPTIONS} value={typeFilter} onChange={setTypeFilter} data-id="element-112" />
          </div>

          {/* Table */}
          <div className="overflow-x-auto" data-id="element-113">
            <table className="w-full text-left border-collapse whitespace-nowrap" data-id="element-114">
              <thead data-id="element-115">
                <tr className="bg-olympian-navy/50 text-[10px] uppercase tracking-wider text-olympian-textMuted border-b border-olympian-border" data-id="element-116">
                  <SortableTh label="Name" className="px-6" data-id="element-117" />
                  <SortableTh label="Phone" data-id="element-118" />
                  <SortableTh label="Email" data-id="element-119" />
                  <SortableTh label="Loan Amount" align="right" data-id="element-120" />
                  <SortableTh label="Type" align="center" data-id="element-121" />
                  <SortableTh label="Rate" align="center" data-id="element-122" />
                  <SortableTh label="Funded" align="center" data-id="element-123" />
                  <SortableTh label="Last Contact" align="center" className="px-6" data-id="element-124" />
                </tr>
              </thead>
              <tbody className="text-sm" data-id="element-125">
                {filteredData.map(row => {
                const isExpanded = expandedId === row.id;
                return <Fragment key={row.id} data-id="element-126">
                      <tr onClick={() => setExpandedId(isExpanded ? null : row.id)} className={`cursor-pointer transition-colors border-b border-olympian-border ${isExpanded ? 'bg-olympian-gold/5' : 'hover:bg-olympian-surface/80 bg-olympian-surface'}`} data-id="element-127">
                        <td className="px-6 py-4" data-id="element-128">
                          <div className="flex items-center gap-3" data-id="element-129">
                            {isExpanded ? <ChevronUpIcon className="w-4 h-4 text-olympian-gold shrink-0" data-id="element-130" /> : <ChevronDownIcon className="w-4 h-4 text-olympian-textMuted shrink-0" data-id="element-131" />}
                            <span className="font-semibold text-theme" data-id="element-132">
                              {row.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-olympian-textMuted font-mono text-xs" data-id="element-133">
                          {row.phone}
                        </td>
                        <td className="px-4 py-4 text-olympian-textMuted text-xs" data-id="element-134">
                          {row.email}
                        </td>
                        <td className="px-4 py-4 text-right font-semibold text-theme font-mono" data-id="element-135">
                          ${row.amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-4 text-center" data-id="element-136">
                          <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border ${row.loanType === 'Conventional' ? 'bg-olympian-navy border-olympian-gold/30 text-olympian-gold' : row.loanType === 'FHA' ? 'bg-olympian-navy border-emerald-500/30 text-emerald-400' : row.loanType === 'VA' ? 'bg-olympian-navy border-blue-500/30 text-blue-400' : 'bg-olympian-navy border-purple-500/30 text-purple-400'}`} data-id="element-137">
                            {row.loanType}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center" data-id="element-138">
                          <span className="px-2.5 py-1 rounded-md text-[10px] font-bold bg-olympian-gold/10 text-olympian-gold border border-olympian-gold/20" data-id="element-139">
                            {row.rate?.toFixed(3)}%
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center text-olympian-textMuted text-xs" data-id="element-140">
                          {new Date(row.originalLoanDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-center" data-id="element-141">
                          {row.lastContact === 'Never' ? <span className="px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-red-500/10 text-red-400 border border-red-500/20" data-id="element-142">
                              Never
                            </span> : <div className="text-xs text-olympian-textMuted flex flex-col items-center" data-id="element-143">
                              <span data-id="element-144">{row.lastContact?.split(' ')[0]}</span>
                              <span className="text-[10px] opacity-70" data-id="element-145">
                                {row.lastContact?.split(' ').slice(1).join(' ')}
                              </span>
                            </div>}
                        </td>
                      </tr>
                      {isExpanded && <tr className="bg-olympian-surface/30 border-b border-olympian-border" data-id="element-146">
                          <td colSpan={8} className="px-12 py-6" data-id="element-147">
                            <div className="max-w-3xl" data-id="element-148">
                              <h4 className="text-xs font-bold text-olympian-gold uppercase tracking-wider mb-4" data-id="element-149">
                                Refinance Talking Points
                              </h4>

                              <div className="space-y-5" data-id="element-150">
                                <div className="flex gap-3" data-id="element-151">
                                  <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0" data-id="element-152">
                                    <TrendingDownIcon className="w-4 h-4 text-emerald-400" data-id="element-153" />
                                  </div>
                                  <div data-id="element-154">
                                    <div className="font-semibold text-theme text-sm mb-0.5" data-id="element-155">
                                      Rate reduction of 0.625%
                                    </div>
                                    <div className="text-xs text-olympian-textMuted" data-id="element-156">
                                      Moving from {row.rate?.toFixed(3)}% to
                                      today's 6.25% rate could save $185/mo
                                      ($2,225/yr).
                                    </div>
                                  </div>
                                </div>

                                <div className="flex gap-3" data-id="element-157">
                                  <div className="w-8 h-8 rounded-lg bg-olympian-gold/10 border border-olympian-gold/20 flex items-center justify-center shrink-0" data-id="element-158">
                                    <DollarSignIcon className="w-4 h-4 text-olympian-gold" data-id="element-159" />
                                  </div>
                                  <div data-id="element-160">
                                    <div className="font-semibold text-theme text-sm mb-0.5" data-id="element-161">
                                      $66,763 in potential lifetime savings
                                    </div>
                                    <div className="text-xs text-olympian-textMuted" data-id="element-162">
                                      On a ${row.amount.toLocaleString()} loan
                                      over 30 years, the rate difference adds up
                                      significantly — a strong reason to act
                                      now.
                                    </div>
                                  </div>
                                </div>

                                <div className="flex gap-3" data-id="element-163">
                                  <div className="w-8 h-8 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center shrink-0" data-id="element-164">
                                    <HomeIcon className="w-4 h-4 text-purple-400" data-id="element-165" />
                                  </div>
                                  <div data-id="element-166">
                                    <div className="font-semibold text-theme text-sm mb-0.5" data-id="element-167">
                                      ~$186,333 estimated equity
                                    </div>
                                    <div className="text-xs text-olympian-textMuted" data-id="element-168">
                                      With an original LTV of 78% and ~32 months
                                      of payments plus estimated appreciation,{' '}
                                      {row.name.split(' ')[0]} has a strong
                                      equity position.
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="mt-6 pt-4 border-t border-olympian-border text-[10px] text-olympian-textMuted" data-id="element-169">
                                Based on today's market rate of 6.25% ·
                                Estimates only, actual terms may vary
                              </div>
                            </div>
                          </td>
                        </tr>}
                    </Fragment>;
              })}
                {filteredData.length === 0 && <tr data-id="element-170">
                    <td colSpan={8} className="px-6 py-12 text-center text-sm text-olympian-textMuted" data-id="element-171">
                      No borrowers match the current filters.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>;
}
function SortableTh({
  label,
  align = 'left',
  className = 'px-4'
}: {
  label: string;
  align?: 'left' | 'right' | 'center';
  className?: string;
}) {
  const justify = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
  return <th className={`${className} py-3 font-semibold`} data-id="element-172">
      <span className={`group inline-flex w-full items-center gap-1 ${justify}`} data-id="element-173">
        {label}
        <ArrowUpDownIcon className="w-3 h-3 opacity-40 transition-opacity group-hover:opacity-70" data-id="element-174" />
      </span>
    </th>;
}
interface FilterDropdownProps {
  label: string;
  options: string[];
  value: string | null;
  onChange: (value: string | null) => void;
}
function FilterDropdown({
  label,
  options,
  value,
  onChange
}: FilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const isActive = value !== null;
  return <div className="relative" ref={ref} data-id="element-175">
      <button onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border transition-colors ${isActive ? 'bg-olympian-gold/10 border-olympian-gold/40 text-olympian-gold' : 'bg-olympian-surface border-olympian-border text-theme hover:bg-olympian-navy'}`} data-id="element-176">
        {isActive ? value : label}
        <ChevronDownIcon className={`w-3.5 h-3.5 ${isActive ? 'text-olympian-gold' : 'text-olympian-textMuted'}`} data-id="element-177" />
      </button>
      {open && <div role="listbox" className="absolute right-0 top-full mt-2 w-52 rounded-lg border border-olympian-border bg-olympian-surface shadow-xl z-50 overflow-hidden animate-crossfade" data-id="element-178">
          <button onClick={() => {
        onChange(null);
        setOpen(false);
      }} className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors ${value === null ? 'text-olympian-gold font-semibold' : 'text-theme hover:bg-white/5'}`} data-id="element-179">
            All {label}
            {value === null && <CheckIcon className="w-3.5 h-3.5" data-id="element-180" />}
          </button>
          <div className="h-px bg-olympian-border" data-id="element-181" />
          {options.map(option => <button key={option} role="option" aria-selected={value === option} onClick={() => {
        onChange(option);
        setOpen(false);
      }} className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors ${value === option ? 'bg-olympian-gold/10 text-olympian-gold font-semibold' : 'text-theme hover:bg-white/5'}`} data-id="element-182">
              {option}
              {value === option && <CheckIcon className="w-3.5 h-3.5" data-id="element-183" />}
            </button>)}
        </div>}
    </div>;
}